import type { ColumnsType } from 'antd/es/table';
import { useCallback, useMemo } from 'react';

import IncrementBadge from '@/components/Badge/IncrementBadge';
import {
  CHART_FREQUENCY,
  CHART_METRIC_VIEW_TYPE,
  CHART_TOOLTIP_TARGET_TYPE,
  CHART_TOTAL_ROW_KEY,
} from '@/entities/chart/model/constants';
import useChartTableMeta from '@/features/(charts)/chart-detail/hooks/use-chart-table-meta';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils, { formatDisplayLabelsForTable } from '@/features/(charts)/chart-renderer/lib/chart-utils';
import TableCellRenderer from '@/features/(charts)/chart-renderer/ui/table-cell-element';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { getTimezoneDayjs } from '@/utils/time';
import { calIncrementAmount, getPercentStringFormat } from '@/utils/util';

import { useOpenChartClickDialog } from '../../hooks/use-open-chart-click-dialog';

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
};

const useChartTimeSeriesTableData = (props: Props) => {
  const openChartClickDialog = useOpenChartClickDialog();
  const { chart } = props;
  const tableData: ChartCrossTableRes = chartUtils.getSortedRowItems(
    chart,
    props.tableData,
    chart.selected_field.items || [],
  ) as ChartCrossTableRes;
  const getLabel = useChartLabel(tableData.field_item_meta);

  const { baseColumns, getLevelRowMap, getTotalRowMap } =
    useChartTableMeta(props);

  const currentDateRange = chartUtils.getTimeRangeByTerm(
    getTimezoneDayjs(),
    chart.target.term,
  );

  const dateLabels = chartUtils.getDateLabelsByUnit({
    unit: chart.target.term.unit,
    dateRange: currentDateRange,
  });

  const compareDateRange = chartUtils.getCompareTimeRangeByTerm(
    getTimezoneDayjs(),
    chart.target.term,
    chart.target.compare_term,
  );

  const compareDateLabels = chartUtils.getDateLabelsByUnit({
    dateRange: compareDateRange,
    unit: chart.target.term.unit,
  });

  const formatter = chartUtils.getFormatByFrequency(
    chart.historical.frequency || CHART_FREQUENCY.MONTH,
  );

  const onCellClick = useCallback(
    (
      x: StringNumberValue | null,
      y: StringNumberValue | null,
      comparedTarget: boolean,
    ) => {
      const modalOptions: ChartFeedbackListModalOptions<CellFeedbackListModalOptions> =
        {
          chart: chart,
          isAll: false,
          cell: {
            compared_target: comparedTarget,
            series_index: 0,
            item_x: x || '',
            item_x_name: getLabel(chart.field.type, x || ''),
            item_y: y || '',
            item_y_name: formatter(getTimezoneDayjs(y?.toString() || '')),
          },
          dashboardCardInfo: props.dashboardCardInfo,
        };
      openChartClickDialog({
        alias: JSON.stringify(modalOptions),
        type: DETAIL_MODAL_TYPE.CHART,
        idx: 0,
        options: modalOptions,
      });
    },
    [chart, formatter, getLabel, openChartClickDialog, props.dashboardCardInfo],
  );

  const isPercentage =
    chart.series[0]?.options?.view_type === CHART_METRIC_VIEW_TYPE.PERCENTAGE;

  const statistics: ColorScaleStatistics = useMemo(() => {
    if (
      typeof chart.series[0]?.options?.scale_min === 'number' &&
      typeof chart.series[0]?.options?.scale_max === 'number'
    ) {
      return {
        max: chart.series[0]?.options?.scale_max,
        min: chart.series[0]?.options?.scale_min,
      };
    }
    const result = {
      max: 0,
      min: 0,
    };

    const reversedTable = chartUtils.transformToYBased(
      tableData.tables[0] || [],
    );
    const reversedCompareTable = chartUtils.transformToYBased(
      tableData.compare_tables?.[0] || [],
    );

    const items = (tableData.tables[0] || []).map((x) => x.x);

    reversedTable.forEach((y) => {
      const yTotal = y.values.reduce((acc, curr) => {
        if (items.includes(curr.x)) {
          acc += curr.value;
        }
        return acc;
      }, 0);
      y.values.forEach((x) => {
        if (!items.includes(x.x)) return;
        const value = isPercentage
          ? Number(getPercentStringFormat(yTotal, x.value))
          : x.value;
        if (typeof value !== 'number') return;
        if (value > result.max) result.max = value;
        if (value < result.min) result.min = value;
      });
    });

    reversedCompareTable.forEach((y) => {
      const yTotal = y.values.reduce((acc, curr) => {
        if (items.includes(curr.x)) {
          acc + curr.value;
        }
        return acc;
      }, 0);
      y.values.forEach((x) => {
        if (!items.includes(x.x)) return;
        const value = isPercentage
          ? Number(getPercentStringFormat(yTotal, x.value))
          : x.value;
        if (typeof value !== 'number') return;
        if (value > result.max) result.max = value;
        if (value < result.min) result.min = value;
      });
    });

    return result;
  }, [chart.series, isPercentage, tableData.compare_tables, tableData.tables]);

  const columns = useMemo((): ColumnsType<any> => {
    const columns: ColumnsType<any> = [...baseColumns];
    const frequency = chart.historical.frequency;
    if (!frequency) throw new Error('Frequency is required');

    const renderer = (
      value: number,
      rowIndex: number,
      columnKey: StringNumberValue,
      comparedTarget: boolean,
      totalCell?: boolean,
    ) => {
      return {
        props: {
          style: {
            backgroundColor:
              !totalCell && chart.series[0]?.options?.color_scale
                ? chartUtils.getColorScaleColor(
                    statistics,
                    value,
                    chart.series[0]?.options?.color_scale,
                    isPercentage,
                  )
                : 'transparent',
          },
        },
        children: (
          <TableCellRenderer
            value={value}
            isPercentage={isPercentage}
            onClick={() => {
              onCellClick(
                (tableData.tables[0] || [])[rowIndex]?.x || null,
                columnKey || null,
                comparedTarget,
              );
            }}
            totalCell={totalCell}
          />
        ),
      };
    };

    // 디스플레이용 포맷 라벨 생성
    const formattedDateLabels = formatDisplayLabelsForTable(dateLabels, frequency);
    const formattedCompareDateLabels = formatDisplayLabelsForTable(compareDateLabels, frequency);

    if (chart.target.is_compare) {
      const compareColumns = dateLabels.map((date, index) => {
        const compareDate = compareDateLabels[index];
        const { start: comparedStartDate } =
          chartUtils.parseDateFormatByFrequency(compareDate, frequency);
        const { start: currentStartDate } =
          chartUtils.parseDateFormatByFrequency(date, frequency);

        return {
          title: `${formattedCompareDateLabels[index]} / ${formattedDateLabels[index]}`,
          dataIndex: date,
          key: date,
          children: [
            {
              title: `Previous`,
              dataIndex: `${compareDate}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`,
              key: `${compareDate}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`,
              width: 100,
              render: (value: number, row: any, index: number) => {
                return renderer(
                  value,
                  index,
                  getTimezoneDayjs(comparedStartDate).toJSON(),
                  true,
                  row.key === CHART_TOTAL_ROW_KEY,
                );
              },
            },
            {
              title: 'Current',
              dataIndex: `${date}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`,
              key: `${date}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`,
              width: 100,
              render: (value: number, row: any, index: number) => {
                return renderer(
                  value,
                  index,
                  getTimezoneDayjs(currentStartDate).toJSON(),
                  false,
                  row.key === CHART_TOTAL_ROW_KEY,
                );
              },
            },
            {
              title: '%Change',
              dataIndex: `${date}-change`,
              key: `${date}-change`,
              width: 100,
              render: (text: number, row: any) => {
                if (row.key === CHART_TOTAL_ROW_KEY) {
                  return null;
                }
                return <IncrementBadge amount={text} />;
              },
            },
          ],
        };
      });
      columns.push(...compareColumns);
    } else {
      columns.push(
        ...dateLabels.map((date, index) => {
          const { start: currentStartDate } =
            chartUtils.parseDateFormatByFrequency(date, frequency);
          return {
            title: formattedDateLabels[index],
            dataIndex: date,
            key: date,
            width: 100,
            render: (value: number, row: any, index: number) => {
              return renderer(
                value,
                index,
                getTimezoneDayjs(currentStartDate).toJSON(),
                false,
                row.key === CHART_TOTAL_ROW_KEY,
              );
            },
          };
        }),
      );
    }

    return columns;
  }, [
    baseColumns,
    chart.historical.frequency,
    chart.target.is_compare,
    chart.series,
    statistics,
    isPercentage,
    onCellClick,
    tableData.tables,
    dateLabels,
    compareDateLabels,
  ]);

  const dataSource = useMemo(() => {
    const isPercentage =
      chart.series[0]?.options?.view_type === CHART_METRIC_VIEW_TYPE.PERCENTAGE;

    const totalMap = chartUtils.getKeyValueMapByKeys(dateLabels);

    (tableData.tables[0] || []).forEach((x) => {
      x.values.find((y) => {
        const formattedDate = formatter(getTimezoneDayjs(y.y.toString()));
        totalMap[formattedDate] += y.value;
      });
    });

    const yTotalMap = chartUtils.getKeyValueMapByKeys(compareDateLabels);

    (tableData.compare_tables?.[0] || []).forEach((x) => {
      x.values.forEach((y) => {
        const formattedDate = formatter(getTimezoneDayjs(y.y.toString()));
        yTotalMap[formattedDate] += y.value;
      });
    });

    if (chart.target.is_compare) {
      const result: Record<string, StringNumberValue | null>[] = (
        tableData.tables[0] || []
      ).map((item) => {
        const dateFields: Record<string, number | null> = {};

        const currentData = item;
        const compareData = (tableData.compare_tables?.[0] || []).find(
          (d) => d.x === item.x,
        );

        dateLabels.forEach((date, index) => {
          const compareDate = compareDateLabels[index];
          dateFields[`${date}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`] = 0;
          dateFields[`${compareDate}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`] = 0;
          dateFields[`${date}-change`] = 0;
        });

        currentData?.values?.forEach((value) => {
          const formattedDate = formatter(getTimezoneDayjs(value.y.toString()));

          dateFields[`${formattedDate}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`] =
            isPercentage
              ? Number(
                  getPercentStringFormat(
                    totalMap[formattedDate],
                    value.value,
                    2,
                  ),
                )
              : value.value;
        });

        compareData?.values?.forEach((value) => {
          const formattedDate = formatter(getTimezoneDayjs(value.y.toString()));
          const comparedValue = isPercentage
            ? Number(
                getPercentStringFormat(
                  yTotalMap[formattedDate],
                  value.value,
                  2,
                ),
              )
            : value.value;

          dateFields[`${formattedDate}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`] =
            comparedValue;
        });

        dateLabels.forEach((date, index) => {
          const compareDate = compareDateLabels[index];
          const current =
            dateFields[`${date}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`];
          const prev =
            dateFields[`${compareDate}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`];
          dateFields[`${date}-change`] = calIncrementAmount(
            current || 0,
            prev || 0,
          );
        });

        const levelRowMap = getLevelRowMap(item.x);
        return {
          key: item.x,
          ...levelRowMap,
          ...dateFields,
        };
      });

      const fields = chartUtils.getKeyValueMapByKeys(dateLabels);
      const compareFields = chartUtils.getKeyValueMapByKeys(compareDateLabels);
      Object.keys(totalMap).forEach((date) => {
        fields[`${date}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`] = isPercentage
          ? Number(getPercentStringFormat(totalMap[date], totalMap[date], 2))
          : Number(totalMap[date].toFixed(1));
      });

      Object.keys(yTotalMap).forEach((date) => {
        compareFields[`${date}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`] =
          isPercentage
            ? Number(
                getPercentStringFormat(yTotalMap[date], yTotalMap[date], 2),
              )
            : Number(yTotalMap[date].toFixed(1));
      });

      result.push({
        key: CHART_TOTAL_ROW_KEY,
        ...getTotalRowMap(),
        ...fields,
        ...compareFields,
      });

      return result;
    }

    const result: Record<string, StringNumberValue | null>[] = (
      tableData.tables[0] || []
    ).map((item) => {
      const fields = chartUtils.getKeyValueMapByKeys(dateLabels);

      const xData = item;

      xData?.values?.forEach((x) => {
        const formattedDate = formatter(getTimezoneDayjs(x.y.toString()));
        fields[formattedDate] = isPercentage
          ? Number(getPercentStringFormat(totalMap[formattedDate], x.value, 2))
          : x.value;
      });
      const levelRowMap = getLevelRowMap(item.x);
      return {
        key: item.x,
        ...levelRowMap,
        ...fields,
      };
    });

    const fields = chartUtils.getKeyValueMapByKeys(dateLabels);
    Object.keys(totalMap).forEach((key) => {
      fields[key] = isPercentage
        ? Number(getPercentStringFormat(totalMap[key], totalMap[key], 2))
        : Number(totalMap[key].toFixed(1));
    });

    result.push({
      key: CHART_TOTAL_ROW_KEY,
      ...getTotalRowMap(),
      ...fields,
    });

    return result;
  }, [
    chart.series,
    chart.target.is_compare,
    compareDateLabels,
    dateLabels,
    formatter,
    getLevelRowMap,
    getTotalRowMap,
    tableData.compare_tables,
    tableData.tables,
  ]);

  return {
    columns,
    dataSource,
  };
};

export default useChartTimeSeriesTableData;
